export const AUTO_ROUTE_MODE = "auto";

const EXPLICIT_ROUTE_MODES = new Set([
  "none",
  "river",
  "road",
  "coastal",
  "international",
  "straight",
]);

const ROUTE_PREFERENCE_MODES = new Set([
  AUTO_ROUTE_MODE,
  "river",
  "road",
  "coastal",
  "international",
  "straight",
]);

const TRANSPORT_FAMILY_MODES = new Set(["road", "river", "sea"]);

const normalizeStringValue = (value) => (
  typeof value === "string" ? value.trim().toLowerCase() : ""
);

export const isRiverCruiseTourType = (tourType) => (
  normalizeStringValue(tourType?.name) === "river cruise"
);

export const isSeaCruiseTourType = (tourType) => (
  normalizeStringValue(tourType?.name) === "sea cruise"
  || Number(tourType?.id) === 2
  || Number(tourType?.id) === 4
);

export const isLandTourType = (tourType) => (
  normalizeStringValue(tourType?.name) === "land tour"
);

export const supportsSegmentRoutingForTourType = (tourType) => (
  isRiverCruiseTourType(tourType)
  || isSeaCruiseTourType(tourType)
  || isLandTourType(tourType)
);

export const getPreferredSeaRouteMode = (tourType) => (
  Number(tourType?.id) === 4 ? "coastal" : "international"
);

export const normalizeExplicitRouteMode = (mode, fallbackMode = "river") => {
  const normalizedMode = normalizeStringValue(mode);
  if (EXPLICIT_ROUTE_MODES.has(normalizedMode)) return normalizedMode;
  const normalizedFallback = normalizeStringValue(fallbackMode);
  if (EXPLICIT_ROUTE_MODES.has(normalizedFallback)) return normalizedFallback;
  return "river";
};

export const normalizeRoutePreferenceMode = (mode, fallbackMode = AUTO_ROUTE_MODE) => {
  const normalizedMode = normalizeStringValue(mode);
  if (ROUTE_PREFERENCE_MODES.has(normalizedMode)) return normalizedMode;
  const normalizedFallback = normalizeStringValue(fallbackMode);
  if (ROUTE_PREFERENCE_MODES.has(normalizedFallback)) return normalizedFallback;
  return AUTO_ROUTE_MODE;
};

export const getSupportedTransportModesForPort = (port) => {
  if (!port) return [];

  const rawModes = Array.isArray(port?.supported_transport_modes)
    ? port.supported_transport_modes
    : [];
  const normalizedModes = rawModes
    .map((item) => normalizeStringValue(typeof item === "string" ? item : item?.mode))
    .filter((mode) => TRANSPORT_FAMILY_MODES.has(mode));
  return normalizedModes.length > 0 ? Array.from(new Set(normalizedModes)) : ["road"];
};

const getAutoPriorityFamilies = (tourType) => {
  if (isRiverCruiseTourType(tourType)) return ["river", "road", "sea"];
  if (isSeaCruiseTourType(tourType)) return ["sea", "road", "river"];
  return ["road", "river", "sea"];
};

export const resolveAutoRouteModeForPorts = ({ startPort, endPort, tourType }) => {
  const startModes = new Set(getSupportedTransportModesForPort(startPort));
  const endModes = new Set(getSupportedTransportModesForPort(endPort));

  for (const family of getAutoPriorityFamilies(tourType)) {
    if (!startModes.has(family) || !endModes.has(family)) continue;
    if (family === "sea") return getPreferredSeaRouteMode(tourType);
    return family;
  }

  return "none";
};

export const resolveSegmentRouteMode = ({
  startPort,
  endPort,
  tourType,
  preferredMode = AUTO_ROUTE_MODE,
}) => {
  const normalizedPreferredMode = normalizeRoutePreferenceMode(preferredMode, AUTO_ROUTE_MODE);
  if (normalizedPreferredMode !== AUTO_ROUTE_MODE) {
    return normalizeExplicitRouteMode(normalizedPreferredMode, "river");
  }
  return resolveAutoRouteModeForPorts({ startPort, endPort, tourType });
};
